import { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Users, GraduationCap, FileCode2, Bot, LogOut, Settings, Menu, X, UserCog, Webhook, Video, ChevronRight, Bell, BarChart3, Brain } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import NotificationIcon from './NotificationIcon';

export default function Navigation() {
  // Single state to manage sidebar state - simplifies animations
  const [sidebarState, setSidebarState] = useState<'mobile-open' | 'collapsed' | 'expanded'>('collapsed');
  const [currentUser, setCurrentUser] = useState<any>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout>();
  
  // Track device type
  const [isMobile, setIsMobile] = useState(false);
  
  // Prevent animation interference with a lock
  const [isAnimating, setIsAnimating] = useState(false);
  const location = useLocation();
  const isClassDetails = /^\/classroom\/[A-Za-z0-9-]+$/.test(location.pathname);
  
  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setCurrentUser(user);
    });
  }, []);

  // Check if we're on mobile at startup and on resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint in Tailwind
    };
    
    // Initial check
    checkIfMobile();
    
    // Check on resize
    window.addEventListener('resize', checkIfMobile);
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);
  
  // Handle sidebar state changes
  const handleOpenSidebar = () => {
    if (isAnimating) return;
    
    // Toggle between open and closed for mobile
    if (isMobile) {
      setSidebarState(sidebarState === 'mobile-open' ? 'collapsed' : 'mobile-open');
    } else {
      setSidebarState('expanded');
    }
  };
  
  const handleCloseSidebar = () => {
    if (isAnimating) return;
    setSidebarState('collapsed');
  };
  
  // Handle mouse enter/leave with minimal debouncing
  const handleMouseEnter = () => {
    // Don't respond to hover events during animations
    if (isAnimating || isMobile) return;
    
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    // Minimal delay to prevent accidental triggers
    hoverTimeoutRef.current = setTimeout(() => {
      setSidebarState('expanded');
    }, 50); // Reduced from 100ms to 50ms for faster response
  };

  const handleMouseLeave = () => {
    if (isAnimating || isMobile) return;
    
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    // Reduced delay for collapse
    hoverTimeoutRef.current = setTimeout(() => {
      setSidebarState('collapsed');
    }, 150); // Reduced from 300ms to 150ms for faster response
  };

  // Handle clicks outside the sidebar to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && 
          !navRef.current.contains(event.target as Node) && 
          sidebarState === 'expanded' &&
          !isAnimating) {
        setSidebarState('collapsed');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sidebarState, isAnimating]);

  const isAdmin = currentUser?.email === 'markofilipovic2003@gmail.com';

  return (
    <>
      {/* Mobile Navigation Controls - only shown on mobile */}
      {isMobile && (
        <>
          {/* Notification Icon - positioned to the left of hamburger menu */}
          {!isClassDetails && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="fixed top-4 right-14 z-50"
            >
              <NotificationIcon />
            </motion.div>
          )}
          
          {/* Mobile Menu Button - now positioned on the right */}
          {!isClassDetails && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              onClick={handleOpenSidebar}
              className="fixed top-4 right-4 z-50 p-2 rounded-full bg-gray-800/80 backdrop-blur-md text-white hover:bg-gray-700 shadow-lg"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={sidebarState === 'mobile-open' ? 'close' : 'menu'}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {sidebarState === 'mobile-open' ? 
                    <X className="w-6 h-6" /> : 
                    <Menu className="w-6 h-6" />}
                </motion.div>
              </AnimatePresence>
            </motion.button>
          )}
        </>
      )}

      {/* Mobile Overlay */}
      <AnimatePresence>
        {sidebarState === 'mobile-open' && (
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            onClick={handleCloseSidebar}
          />
        )}
      </AnimatePresence>
      
      {/* Main Navigation Content */}
      <AnimatePresence mode="wait">
        {/* Collapsed Sidebar - pill style */}
        {sidebarState === 'collapsed' && !isMobile && (
          <motion.div 
            key="collapsed"
            initial={{ width: 0, opacity: 0, x: -20 }}
            animate={{ 
              width: '64px', 
              opacity: 1, 
              x: 0,
              transition: { 
                type: 'spring', 
                stiffness: 300,
                damping: 30,
                duration: 0.4 
              }
            }}
            exit={{ 
              width: 0, 
              opacity: 0, 
              x: -20,
              transition: { 
                duration: 0.2,
                ease: 'easeInOut' 
              }
            }}
            onAnimationStart={() => setIsAnimating(true)}
            onAnimationComplete={() => setIsAnimating(false)}
            className="fixed left-6 top-0 bottom-0 my-auto h-fit flex flex-col items-center rounded-full 
                    bg-[#0d1117]/70 backdrop-blur-xl border border-white/10 shadow-xl
                    overflow-hidden z-40 hover:bg-[#0d1117]/60 transition-colors duration-300"
            style={{
              boxShadow: `0 0 15px 0 rgba(80, 200, 255, 0.2), 
                        0 0 30px 10px rgba(80, 200, 255, 0.07)`,
              background: `linear-gradient(145deg, rgba(20, 30, 48, 0.7), rgba(13, 17, 23, 0.8))`,
            }}
            onMouseEnter={handleMouseEnter}
          >
            <div className="flex flex-col items-center gap-6 py-6">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2a3a4a] to-[#0f172a] flex items-center justify-center border border-white/10">
                <img
                  src="https://i.postimg.cc/25s922XT/image.png"
                  alt="Logo"
                  className="w-8 h-8 object-contain"
                />
              </div>
              
              <div className="flex flex-col items-center gap-6">
                {/* Collapsed: Ai Agent at top with subtle glow */}
                <NavLink
                  to="/agent"
                  className={({ isActive }) =>
                    `w-10 h-10 flex items-center justify-center rounded-full transition-all ring-1 ring-[#c9fffc]/20 ${
                      isActive
                        ? 'bg-[#c9fffc]/30 text-[#c9fffc]'
                        : 'text-[#c9fffc]/90 bg-[#c9fffc]/10'
                    } agent-glow agent-pulse`
                  }
                  aria-label="Ai Agent"
                >
                  <Brain className="w-5 h-5" />
                </NavLink>
                
                <NavLink to="/community" className={({ isActive }) => 
                  `w-10 h-10 flex items-center justify-center rounded-full transition-all ${isActive ? 'bg-[#c9fffc]/20 text-[#c9fffc]' : 'text-gray-400 hover:bg-white/5'}`
                }>
                  <Users className="w-5 h-5" />
                </NavLink>
                
                <NavLink to="/classroom" className={({ isActive }) => 
                  `w-10 h-10 flex items-center justify-center rounded-full transition-all ${isActive ? 'bg-[#c9fffc]/20 text-[#c9fffc]' : 'text-gray-400 hover:bg-white/5'}`
                }>
                  <GraduationCap className="w-5 h-5" />
                </NavLink>
                
                <NavLink to="/blueprints" className={({ isActive }) => 
                  `w-10 h-10 flex items-center justify-center rounded-full transition-all ${isActive ? 'bg-[#c9fffc]/20 text-[#c9fffc]' : 'text-gray-400 hover:bg-white/5'}`
                }>
                  <FileCode2 className="w-5 h-5" />
                </NavLink>
                
                <NavLink to="/ai-tool" className={({ isActive }) => 
                  `w-10 h-10 flex items-center justify-center rounded-full transition-all ${isActive ? 'bg-[#c9fffc]/20 text-[#c9fffc]' : 'text-gray-400 hover:bg-white/5'}`
                }>
                  <Bot className="w-5 h-5" />
                </NavLink>
              </div>
              
              <ChevronRight className="w-5 h-5 text-gray-400 mt-6" />
            </div>
          </motion.div>
        )}

        {/* Expanded Sidebar */}
        {(sidebarState === 'expanded' || sidebarState === 'mobile-open') && (
          <motion.nav 
            key="expanded"
            ref={navRef}
            initial={{ 
              width: isMobile ? 0 : '64px', 
              opacity: 0.8,
              x: isMobile ? -50 : 0
            }}
            animate={{ 
              width: isMobile ? '85vw' : '280px', 
              maxWidth: '320px',
              opacity: 1,
              x: 0,
              transition: { 
                type: 'spring', 
                stiffness: 300,
                damping: 30,
                duration: 0.4 
              }
            }}
            exit={{ 
              width: isMobile ? 0 : '64px', 
              opacity: 0,
              x: isMobile ? -50 : 0,
              transition: { 
                duration: 0.25,
                ease: 'easeInOut' 
              }
            }}
            onAnimationStart={() => setIsAnimating(true)}
            onAnimationComplete={() => setIsAnimating(false)}
            className={`${isMobile ? 'fixed left-0 top-0 h-full rounded-r-3xl' : 'fixed left-6 top-0 bottom-0 my-auto h-fit max-h-[min(90vh,800px)] rounded-3xl'} 
                      bg-[#0d1117]/75 backdrop-blur-xl border border-white/10 
                      overflow-hidden z-40 shadow-2xl flex flex-col`}
            onMouseLeave={!isMobile ? handleMouseLeave : undefined}
            style={{
              boxShadow: `0 0 20px 0 rgba(80, 200, 255, 0.2), 
                         0 0 50px 10px rgba(80, 200, 255, 0.07)`,
              background: `linear-gradient(145deg, rgba(20, 30, 48, 0.75), rgba(13, 17, 23, 0.85))`,
            }}
          >
        <div className="flex flex-col h-full overflow-y-auto custom-scrollbar">
          <div className="flex items-center mb-6 pt-6 px-6">
            <div className="flex items-center gap-3">
              <motion.div 
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2a3a4a] to-[#0f172a] flex items-center justify-center border border-white/10"
              >
                <img
                  src="https://i.postimg.cc/25s922XT/image.png"
                  alt="Community Logo"
                  className="w-8 h-8 object-contain"
                />
              </motion.div>
              <motion.span 
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="text-xl font-bold text-white"
              >
                Community
              </motion.span>
            </div>
          </div>

        <div className="flex-1 space-y-1 px-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ staggerChildren: 0.05, delayChildren: 0.1 }}
            className="space-y-1"
          >
            {/* Expanded: Ai Agent at top with glow */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              <NavLink
                to="/agent"
                onClick={() => isMobile ? setSidebarState('collapsed') : null}
                className={({ isActive }) =>
                  `flex items-center px-4 py-3 rounded-xl transition-all ring-1 ring-[#c9fffc]/20 ${
                    isActive
                      ? 'bg-[#c9fffc]/15 text-[#c9fffc] shadow-glow'
                      : 'text-[#c9fffc] bg-[#c9fffc]/5'
                  } agent-glow agent-pulse`
                }
              >
                <Brain className="w-5 h-5 mr-3" />
                <span className="font-medium">Ai Agent</span>
              </NavLink>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              <NavLink
                to="/community"
                onClick={() => isMobile ? setSidebarState('collapsed') : null}
                className={({ isActive }) =>
                  `flex items-center px-4 py-3 text-gray-300 hover:bg-white/10 rounded-xl transition-all ${
                    isActive ? 'bg-[#c9fffc]/10 text-[#c9fffc] shadow-glow' : ''
                  }`
                }
              >
                <Users className="w-5 h-5 mr-3" />
                <span className="font-medium">Community</span>
              </NavLink>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: 0.05 }}
            >
              <NavLink
                to="/classroom"
                onClick={() => isMobile ? setSidebarState('collapsed') : null}
                className={({ isActive }) =>
                  `flex items-center px-4 py-3 text-gray-300 hover:bg-white/10 rounded-xl transition-all ${
                    isActive ? 'bg-[#c9fffc]/10 text-[#c9fffc] shadow-glow' : ''
                  }`
                }
              >
                <GraduationCap className="w-5 h-5 mr-3" />
                <span className="font-medium">Classroom</span>
              </NavLink>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: 0.1 }}
            >
              <NavLink
                to="/blueprints"
                onClick={() => isMobile ? setSidebarState('collapsed') : null}
                className={({ isActive }) =>
                  `flex items-center px-4 py-3 text-gray-300 hover:bg-white/10 rounded-xl transition-all ${
                    isActive ? 'bg-[#c9fffc]/10 text-[#c9fffc] shadow-glow' : ''
                  }`
                }
              >
                <FileCode2 className="w-5 h-5 mr-3" />
                <span className="font-medium">Blueprints</span>
              </NavLink>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: 0.15 }}
            >
              <NavLink
                to="/ai-tool"
                onClick={() => isMobile ? setSidebarState('collapsed') : null}
                className={({ isActive }) =>
                  `flex items-center px-4 py-3 text-gray-300 hover:bg-white/10 rounded-xl transition-all ${
                    isActive ? 'bg-[#c9fffc]/10 text-[#c9fffc] shadow-glow' : ''
                  }`
                }
              >
                <Bot className="w-5 h-5 mr-3" />
                <span className="font-medium">AI Tools</span>
              </NavLink>
            </motion.div>

            
            
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: 0.2 }}
            >
              <NavLink
                to="/live-events"
                onClick={() => isMobile ? setSidebarState('collapsed') : null}
                className={({ isActive }) =>
                  `flex items-center px-4 py-3 text-gray-300 hover:bg-white/10 rounded-xl transition-all ${
                    isActive ? 'bg-[#c9fffc]/10 text-[#c9fffc] shadow-glow' : ''
                  }`
                }
              >
                <Video className="w-5 h-5 mr-3" />
                <span className="font-medium">Live Events</span>
              </NavLink>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: 0.25 }}
            >
              <NavLink
                to="/settings"
                onClick={() => isMobile ? setSidebarState('collapsed') : null}
                className={({ isActive }) =>
                  `flex items-center px-4 py-3 text-gray-300 hover:bg-white/10 rounded-xl transition-all ${
                    isActive ? 'bg-[#c9fffc]/10 text-[#c9fffc] shadow-glow' : ''
                  }`
                }
              >
                <Settings className="w-5 h-5 mr-3" />
                <span className="font-medium">Settings</span>
              </NavLink>
            </motion.div>
          </motion.div>
        </div>

        {isAdmin && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div className="px-6 py-3 mt-2">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Admin Features
              </h3>
            </div>
            <div className="space-y-1 px-4">
              <NavLink
                to="/users"
                onClick={() => isMobile ? setSidebarState('collapsed') : null}
                className={({ isActive }) =>
                  `flex items-center px-4 py-3 text-gray-300 hover:bg-white/10 rounded-xl transition-all ${
                    isActive ? 'bg-[#c9fffc]/10 text-[#c9fffc] shadow-glow' : ''
                  }`
                }
              >
                <UserCog className="w-5 h-5 mr-3" />
                <span className="font-medium">Manage Users</span>
              </NavLink>
              <NavLink
                to="/admin/live-events"
                onClick={() => isMobile ? setSidebarState('collapsed') : null}
                className={({ isActive }) =>
                  `flex items-center px-4 py-3 text-gray-300 hover:bg-white/10 rounded-xl transition-all ${
                    isActive ? 'bg-[#c9fffc]/10 text-[#c9fffc] shadow-glow' : ''
                  }`
                }
              >
                <Video className="w-5 h-5 mr-3" />
                <span className="font-medium">Live Events</span>
              </NavLink>
              <NavLink
                to="/webhooks"
                onClick={() => isMobile ? setSidebarState('collapsed') : null}
                className={({ isActive }) =>
                  `flex items-center px-4 py-3 text-gray-300 hover:bg-white/10 rounded-xl transition-all ${
                    isActive ? 'bg-[#c9fffc]/10 text-[#c9fffc] shadow-glow' : ''
                  }`
                }
              >
                <Webhook className="w-5 h-5 mr-3" />
                <span className="font-medium">Manage Webhooks</span>
              </NavLink>
              <NavLink
                to="/notifications"
                onClick={() => isMobile ? setSidebarState('collapsed') : null}
                className={({ isActive }) =>
                  `flex items-center px-4 py-3 text-gray-300 hover:bg-white/10 rounded-xl transition-all ${
                    isActive ? 'bg-[#c9fffc]/10 text-[#c9fffc] shadow-glow' : ''
                  }`
                }
              >
                <Bell className="w-5 h-5 mr-3" />
                <span className="font-medium">Send Notifications</span>
              </NavLink>
              <NavLink
                to="/admin/dashboard"
                onClick={() => isMobile ? setSidebarState('collapsed') : null}
                className={({ isActive }) =>
                  `flex items-center px-4 py-3 text-gray-300 hover:bg-white/10 rounded-xl transition-all ${
                    isActive ? 'bg-[#c9fffc]/10 text-[#c9fffc] shadow-glow' : ''
                  }`
                }
              >
                <BarChart3 className="w-5 h-5 mr-3" />
                <span className="font-medium">Dashboard</span>
              </NavLink>
            </div>
          </motion.div>
        )}

        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          onClick={handleSignOut}
          className="flex items-center mx-4 px-4 py-3 my-4 text-gray-300 hover:bg-red-500/10 hover:text-red-300 rounded-xl transition-all"
        >
          <LogOut className="w-5 h-5 mr-3" />
          <span className="font-medium">Sign Out</span>
        </motion.button>
      </div>
      
            </motion.nav>
        )}
      </AnimatePresence>
      
      {/* Custom scrollbar and shadow effects */}
      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: rgba(155, 155, 155, 0.3);
          border-radius: 20px;
        }
        .shadow-glow {
          box-shadow: 0 0 8px 0 rgba(201, 255, 252, 0.2);
        }
        @keyframes pulse-border {
          0% { border-color: rgba(255, 255, 255, 0.1); }
          50% { border-color: rgba(255, 255, 255, 0.2); }
          100% { border-color: rgba(255, 255, 255, 0.1); }
        }

        /* AI Agent emphasis styles */
        .agent-glow {
          box-shadow: 0 0 12px rgba(201, 255, 252, 0.35), 0 0 24px rgba(80, 200, 255, 0.18);
          transition: box-shadow 0.25s ease, transform 0.25s ease;
        }
        .agent-glow:hover {
          box-shadow: 0 0 14px rgba(201, 255, 252, 0.5), 0 0 28px rgba(80, 200, 255, 0.26);
          transform: translateY(-1px);
        }
        @keyframes agentPulse {
          0% { box-shadow: 0 0 0 0 rgba(201, 255, 252, 0.35); }
          70% { box-shadow: 0 0 0 12px rgba(201, 255, 252, 0); }
          100% { box-shadow: 0 0 0 0 rgba(201, 255, 252, 0); }
        }
        .agent-pulse {
          animation: agentPulse 2.6s ease-in-out infinite;
        }
      `}} />
    </>
  );
}